import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { PermissionsProvider } from "@/context/PermissionsContext";
import ModalCampaignProvider from "@/components/modal-campaigns/ModalCampaignProvider";
import ErrorBoundary from "@/components/ErrorBoundary";

// Layouts
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import ProtectedRoute from "@/components/ProtectedRoute";

// Pages
import Login from "@/pages/Login";
import Register from "@/pages/Register";

import Dashboard from "@/pages/Dashboard";
import Inventory from "@/pages/Inventory";
import Notifications from "@/pages/Notifications";
import Reports from "@/pages/Reports";
import NotFound from "@/pages/NotFound";
import VoorraadCheck from "@/pages/VoorraadCheck";
import NotificationDetail from "@/pages/NotificationDetail";
import Medewerkers from "@/pages/Medewerkers";
import Rollenbeheer from "@/pages/Rollenbeheer";
import Feedback from "@/pages/Feedback";
import PackagePickup from "@/pages/PackagePickup";
import ModalCampaignAdmin from "@/pages/ModalCampaignAdmin";
import LiveDashboard from "@/pages/LiveDashboard";
import AdminRegistraties from "@/pages/AdminRegistraties";
import AdminGebruikersBeheer from "@/pages/AdminGebruikersBeheer";
import ProfielSessies from "@/pages/ProfielSessies";
import WorkflowExecutions from "@/pages/WorkflowExecutions";
import ApiDocumentation from "@/pages/ApiDocumentation";
import ExpiredProducts from "@/pages/ExpiredProducts";
import NewProducts from "@/pages/NewProducts";
import Orders from "@/pages/Orders";
import OrdersLuijtenVvz from "@/pages/OrdersLuijtenVvz";
import KleurenOrders from "@/pages/Packers";
import TestInkoop from "@/pages/TestInkoop";

// Create a query client with some default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Restructure AppRoutes to ensure it's inside the AuthProvider and other providers
const App = () => (
  <ErrorBoundary>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <PermissionsProvider>
            <TooltipProvider>
              <ModalCampaignProvider>
                <Toaster />
                <Sonner />
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/test-inkoop" element={<TestInkoop />} />
                
                
                {/* Protected Dashboard Layout Routes */}
                <Route element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/live-dashboard" element={<LiveDashboard />} />
                  <Route path="/inventory" element={
                    <ProtectedRoute 
                      requiredResource="inventory"
                    >
                      <Inventory />
                    </ProtectedRoute>
                  } />
                  <Route path="/notifications" element={
                    <ProtectedRoute 
                      requiredResource="notifications"
                    >
                      <Notifications />
                    </ProtectedRoute>
                  } />
                  <Route path="/feedback" element={
                    <ProtectedRoute 
                      requiredResource="feedback"
                    >
                      <Feedback />
                    </ProtectedRoute>
                  } />
                  <Route path="/package-pickup" element={
                    <ProtectedRoute 
                      requiredResource="packagePickup"
                    >
                      <PackagePickup />
                    </ProtectedRoute>
                  } />
                  <Route path="/reports" element={
                    <ProtectedRoute 
                      requiredResource="reports"
                    >
                      <Reports />
                    </ProtectedRoute>
                  } />
                  <Route path="/medewerkers" element={
                    <ProtectedRoute 
                      requiredResource="medewerkers"
                    >
                      <Medewerkers />
                    </ProtectedRoute>
                  } />
                  <Route path="/rollenbeheer" element={
                    <ProtectedRoute requiredResource="rollenbeheer">
                      <Rollenbeheer />
                    </ProtectedRoute>
                  } />
                  <Route path="/modal-campagnes" element={
                    <ProtectedRoute requiredResource="nieuws">
                      <ModalCampaignAdmin />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/registraties" element={
                    <ProtectedRoute requiredResource="registraties">
                      <AdminRegistraties />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/gebruikers" element={
                    <ProtectedRoute requiredResource="gebruikersbeheer">
                      <AdminGebruikersBeheer />
                    </ProtectedRoute>
                  } />
                  <Route path="/profiel/sessies" element={<ProfielSessies />} />
                  <Route path="/workflow-executions" element={
                    <ProtectedRoute requiredResource="workflowExecutions">
                      <WorkflowExecutions />
                    </ProtectedRoute>
                  } />
                  <Route path="/expired-products" element={
                    <ProtectedRoute requiredResource="expiredProducts">
                      <ExpiredProducts />
                    </ProtectedRoute>
                  } />
                  <Route path="/nieuwe-producten" element={
                    <ProtectedRoute requiredResource="nieuweProducten">
                      <NewProducts />
                    </ProtectedRoute>
                  } />
                  <Route path="/orders" element={
                    <ProtectedRoute requiredResource="ordersDecoprof">
                      <Orders />
                    </ProtectedRoute>
                  } />
                  <Route path="/orders/decoprof" element={
                    <ProtectedRoute requiredResource="ordersDecoprof">
                      <Orders />
                    </ProtectedRoute>
                  } />
                  <Route path="/orders/luijten-vvz" element={
                    <ProtectedRoute requiredResource="ordersLuijtenVvz">
                      <OrdersLuijtenVvz />
                    </ProtectedRoute>
                  } />
                  <Route path="/orders/*" element={<Navigate to="/orders" replace />} />
                  <Route path="/kleuren-orders" element={
                    <ProtectedRoute requiredResource="kleurenOrders">
                      <KleurenOrders />
                    </ProtectedRoute>
                  } />
                  
                  {/* Dashboard Detail Pages */}
                  <Route path="/voorraad-check" element={<VoorraadCheck />} />
                  <Route path="/notification-detail/:id" element={
                    <ProtectedRoute 
                      requiredResource="notifications"
                    >
                      <NotificationDetail />
                    </ProtectedRoute>
                  } />
                  <Route path="/api-docs" element={
                    <ProtectedRoute requiredResource="apiDocs">
                      <ApiDocumentation />
                    </ProtectedRoute>
                  } />
                </Route>
                
                {/* 404 Route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </ModalCampaignProvider>
          </TooltipProvider>
        </PermissionsProvider>
      </AuthProvider>
    </QueryClientProvider>
  </BrowserRouter>
  </ErrorBoundary>
);

export default App;
